import { useMemo, useState } from 'react'
import { useSelectedShop } from '../ShopContext'
import { useMarkShipped, deriveOrderState, type Order, type OrderState } from '../useOrders'

// OrderDetailScreen — full detail view for a single order, pushed by
// OrdersScreen when a card is tapped. Renders buyer + shipping + items +
// total + mark-shipped affordance.
//
// Mark-shipped is native-only. Shopify orders (source === 'shopify') show
// a "Manage in Shopify" link out to the merchant's Shopify Admin instead —
// fulfillment there is Shopify's job, ours would be a separate API call
// (deferred until we hear merchants ask for in-app Shopify fulfillment).
//
// Navigation model: this component is rendered by the parent OrdersScreen
// when it has a selected order; the back button just clears that state.
// No router — Wave 4's shell is a single bottom-tab shell, not URL-based.

interface Props {
  order:      Order
  onBack:     () => void
  /**
   * Fires after a successful mark-shipped so the parent OrdersScreen can
   * kick a background refresh — user then sees the updated pill when they
   * navigate back.
   */
  onChanged?: () => void
}

export function OrderDetailScreen({ order: initialOrder, onBack, onChanged }: Props) {
  const shop = useSelectedShop()
  // Local order state — refreshed in place after mark-shipped success so
  // the detail screen reflects the new tracking # without needing a
  // full-list roundtrip. The list picks up the same change on next refresh.
  const [order, setOrder] = useState<Order>(initialOrder)
  const orderState = useMemo(() => deriveOrderState(order), [order])
  const isShopify  = order.source === 'shopify'

  return (
    <div style={s.wrap}>
      <div style={s.topBar}>
        <button style={s.backBtn} onClick={onBack} aria-label="Back">← Back</button>
      </div>

      <div style={s.header}>
        <StatusPill state={orderState} order={order} />
        <div style={s.orderId}>Order {shortId(order.shop_order_id)}</div>
      </div>

      <Section label="Buyer">
        <div style={s.buyerName}>
          {order.shipping_address?.name?.trim() || order.buyer_email || '—'}
        </div>
        {order.buyer_email && order.shipping_address?.name && (
          <div style={s.buyerSub}>{order.buyer_email}</div>
        )}
      </Section>

      {order.shipping_address?.address && (
        <Section label="Shipping to">
          {order.shipping_address.name && (
            <div style={s.addrLine}>{order.shipping_address.name}</div>
          )}
          {order.shipping_address.address.line1 && (
            <div style={s.addrLine}>{order.shipping_address.address.line1}</div>
          )}
          {order.shipping_address.address.line2 && (
            <div style={s.addrLine}>{order.shipping_address.address.line2}</div>
          )}
          <div style={s.addrLine}>
            {[
              order.shipping_address.address.city,
              order.shipping_address.address.state,
              order.shipping_address.address.postal_code,
            ].filter(Boolean).join(', ')}
          </div>
          {order.shipping_address.address.country && (
            <div style={s.addrLine}>{order.shipping_address.address.country}</div>
          )}
        </Section>
      )}

      <Section label="Items">
        {(order.items?.length ?? 0) === 0 ? (
          <div style={s.itemsEmpty}>{order.product_type || 'Item'}</div>
        ) : (
          <div style={s.itemsList}>
            {order.items.map((item, i) => (
              <div key={i} style={s.itemRow}>
                <div style={s.itemMain}>
                  <div style={s.itemName}>
                    {item.product_name}
                    {item.quantity > 1 && <span style={s.itemQty}> × {item.quantity}</span>}
                  </div>
                  {item.sku && <div style={s.itemSku}>{item.sku}</div>}
                </div>
                <div style={s.itemPrice}>
                  ${((item.amount_cents * item.quantity) / 100).toFixed(2)}
                </div>
              </div>
            ))}
            <div style={s.totalRow}>
              <div style={s.totalLabel}>Total</div>
              <div style={s.totalValue}>${((order.amount_cents ?? 0) / 100).toFixed(2)}</div>
            </div>
          </div>
        )}
      </Section>

      {order.created_at && (
        <Section label="Placed">
          <div style={s.metaLine}>{new Date(order.created_at).toLocaleString()}</div>
        </Section>
      )}

      {/* Fulfillment section — mark-shipped for native orders, tracking
          display for already-shipped orders, or an out-link to Shopify. */}
      {orderState === 'shipped' ? (
        <Section label="Shipped">
          {order.tracking_number ? (
            <div style={s.metaLine}>Tracking: <span style={s.mono}>{order.tracking_number}</span></div>
          ) : (
            <div style={s.metaLine}>Marked shipped</div>
          )}
          {order.shipped_at && (
            <div style={s.metaSub}>{new Date(order.shipped_at).toLocaleString()}</div>
          )}
        </Section>
      ) : orderState === 'needs-shipping' && isShopify ? (
        <Section label="Fulfillment">
          <div style={s.hint}>
            This order was placed through Shopify. Mark as shipped in your
            Shopify Admin — the status will sync back within an hour.
          </div>
          <a
            href={`https://${shop?.handle ?? ''}.myshopify.com/admin/orders`}
            target="_blank"
            rel="noopener noreferrer"
            style={s.linkBtn}
          >
            Open Shopify Admin ↗
          </a>
        </Section>
      ) : orderState === 'needs-shipping' ? (
        <MarkShippedForm
          handle={shop?.handle ?? null}
          orderId={order.shop_order_id}
          onSuccess={updated => {
            setOrder(prev => ({
              ...prev,
              tracking_number: updated.tracking_number ?? prev.tracking_number,
              shipped_at:      updated.shipped_at      ?? prev.shipped_at,
            }))
            onChanged?.()
          }}
        />
      ) : null}
    </div>
  )
}

// ── MarkShippedForm ────────────────────────────────────────────────────────

interface FormProps {
  handle:    string | null
  orderId:   string
  onSuccess: (order: Order) => void
}

function MarkShippedForm({ handle, orderId, onSuccess }: FormProps) {
  const { state, submit, reset } = useMarkShipped(handle, orderId)
  const [tracking, setTracking] = useState('')
  const busy = state.status === 'submitting'

  // Bubble success up so the parent can refresh + update the local order.
  if (state.status === 'success') {
    onSuccess(state.order)
    reset()   // clear so the form doesn't keep firing onSuccess on every render
    return null
  }

  return (
    <div style={s.section}>
      <div style={s.sectionLabel}>Ship this order</div>
      <div style={s.card}>
        <input
          type="text"
          inputMode="text"
          autoComplete="off"
          autoCapitalize="none"
          placeholder="Tracking number"
          value={tracking}
          onChange={e => setTracking(e.target.value)}
          disabled={busy}
          style={s.input}
        />
        {state.status === 'error' && (
          <div style={s.formErr}>{state.message}</div>
        )}
        <button
          style={{ ...s.primaryBtn, ...(busy || !tracking.trim() ? s.primaryBtnDisabled : {}) }}
          onClick={() => submit(tracking)}
          disabled={busy || !tracking.trim()}
        >
          {busy ? 'Marking shipped…' : 'Mark shipped'}
        </button>
      </div>
    </div>
  )
}

// ── Bits ───────────────────────────────────────────────────────────────────

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={s.section}>
      <div style={s.sectionLabel}>{label}</div>
      <div style={s.card}>{children}</div>
    </div>
  )
}

function StatusPill({ state, order }: { state: OrderState; order: Order }) {
  const cfg = STATUS_CONFIG[state]
  const suffix = state === 'shipped' && order.tracking_number ? ` · ${order.tracking_number}` : ''
  return (
    <span style={{ ...s.pill, background: cfg.bg, color: cfg.color }}>
      <span style={s.pillDot}>●</span>
      {cfg.label}{suffix}
    </span>
  )
}

const STATUS_CONFIG: Record<OrderState, { label: string; bg: string; color: string }> = {
  'needs-shipping': { label: 'Needs shipping', bg: '#3a2f1e', color: '#e5b880' },
  'shipped':        { label: 'Shipped',        bg: '#1e3a24', color: '#a5d8b0' },
  'returned':       { label: 'Return',         bg: '#3a1e1e', color: '#f5b7b7' },
  'refunded':       { label: 'Refunded',       bg: '#2a2a2a', color: '#aaaaaa' },
}

// Trim UUIDs to a short display slug for the header. Full ID is still on
// the wire; this is just cosmetic.
function shortId(id: string): string {
  if (id.length <= 8) return `#${id}`
  return `#${id.slice(0, 8)}`
}

// ── Styles ─────────────────────────────────────────────────────────────────

const s = {
  wrap: {
    padding: '16px 20px 40px',
  },
  topBar: {
    marginBottom: 16,
  },
  backBtn: {
    padding: '6px 12px 6px 4px',
    fontSize: 14,
    fontWeight: 600,
    background: 'transparent',
    color: '#1a1a18',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  header: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 8,
    marginBottom: 20,
  },
  orderId: {
    fontSize: 20,
    fontWeight: 700,
    color: '#1a1a18',
    fontFamily: '"SF Mono", Consolas, monospace',
  },

  section: {
    marginBottom: 20,
  },
  sectionLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: '#8a8a80',
    textTransform: 'uppercase' as const,
    letterSpacing: 0.5,
    marginBottom: 8,
    padding: '0 4px',
  },
  card: {
    background: '#ffffff',
    border: '1px solid #e5e5e0',
    borderRadius: 12,
    padding: 16,
  },

  buyerName: {
    fontSize: 16,
    fontWeight: 600,
    color: '#1a1a18',
  },
  buyerSub: {
    fontSize: 13,
    color: '#8a8a80',
    marginTop: 4,
    fontFamily: '"SF Mono", Consolas, monospace',
  },
  addrLine: {
    fontSize: 14,
    color: '#1a1a18',
    lineHeight: 1.5,
  },

  itemsList: {
    display: 'flex',
    flexDirection: 'column' as const,
  },
  itemsEmpty: {
    fontSize: 14,
    color: '#5a5a55',
  },
  itemRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 12,
    padding: '8px 0',
    borderBottom: '1px solid #f0efe9',
  },
  itemMain: {
    flex: 1,
    minWidth: 0,
  },
  itemName: {
    fontSize: 14,
    color: '#1a1a18',
  },
  itemQty: {
    color: '#5a5a55',
  },
  itemSku: {
    fontSize: 11,
    color: '#8a8a80',
    marginTop: 3,
    fontFamily: '"SF Mono", Consolas, monospace',
  },
  itemPrice: {
    fontSize: 14,
    fontWeight: 600,
    color: '#1a1a18',
    fontVariantNumeric: 'tabular-nums' as const,
    flexShrink: 0,
  },
  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: 12,
    marginTop: 4,
  },
  totalLabel: {
    fontSize: 14,
    fontWeight: 600,
    color: '#1a1a18',
  },
  totalValue: {
    fontSize: 18,
    fontWeight: 700,
    color: '#1a1a18',
    fontVariantNumeric: 'tabular-nums' as const,
  },

  metaLine: {
    fontSize: 14,
    color: '#1a1a18',
  },
  metaSub: {
    fontSize: 12,
    color: '#8a8a80',
    marginTop: 4,
  },
  mono: {
    fontFamily: '"SF Mono", Consolas, monospace',
    fontSize: 13,
    color: '#1a1a18',
  },
  hint: {
    fontSize: 13,
    color: '#5a5a55',
    lineHeight: 1.5,
    marginBottom: 12,
  },

  input: {
    width: '100%',
    padding: '12px 14px',
    fontSize: 15,
    border: '1px solid #d5d5d0',
    borderRadius: 8,
    fontFamily: 'inherit',
    boxSizing: 'border-box' as const,
    outline: 'none',
    marginBottom: 12,
  },
  primaryBtn: {
    width: '100%',
    padding: '14px 20px',
    fontSize: 15,
    fontWeight: 600,
    background: '#1a1a18',
    color: '#faf9f6',
    border: 'none',
    borderRadius: 10,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  primaryBtnDisabled: {
    background: '#c5c5c0',
    color: '#faf9f6',
    cursor: 'not-allowed',
  },
  linkBtn: {
    display: 'inline-block',
    padding: '10px 14px',
    fontSize: 14,
    fontWeight: 600,
    background: '#95bf47',
    color: '#ffffff',
    borderRadius: 8,
    textDecoration: 'none',
  },
  formErr: {
    padding: '8px 12px',
    marginBottom: 12,
    background: '#3a1e1e',
    color: '#f5b7b7',
    borderRadius: 6,
    fontSize: 13,
  },

  pill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    padding: '4px 10px',
    borderRadius: 12,
    fontSize: 12,
    fontWeight: 600,
    alignSelf: 'flex-start' as const,
  },
  pillDot: {
    fontSize: 8,
    lineHeight: 1,
  },
} as const
